import { Box, Button, Card, Group, SimpleGrid, Stack, Text } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { fetchApiLiveness } from '../api/health';
import { useAuth } from '../auth/AuthContext';
import { PageTitle } from '../components/PageTitle';
import { ru } from '../i18n/ru';

export function HomePage() {
  const { user, isAuthenticated } = useAuth();

  // Оплата доступна только вошедшему родителю: гостя ссылка увела бы
  // на форму входа с непонятного места.
  const sections = [
    { title: ru.home.cards.newsTitle, text: ru.home.cards.newsText, to: '/news' },
    {
      title: ru.home.cards.paymentTitle,
      text: ru.home.cards.paymentText,
      to: isAuthenticated ? '/payments' : null,
    },
    { title: ru.home.cards.docsTitle, text: ru.home.cards.docsText, to: null },
  ];

  const { data, isPending, isError } = useQuery({
    queryKey: ['api-liveness'],
    queryFn: fetchApiLiveness,
    retry: false,
  });

  // Зелёная плашка «всё хорошо» — шум на странице для родителя, поэтому
  // сообщаем только о проблеме: она объясняет, почему ничего не работает.
  const apiIsDown = !isPending && (isError || !data);

  return (
    <Stack gap={48}>
      <Stack gap="xl" pt={{ base: 'md', sm: 40 }}>
        {/* Знак студии остаётся только в шапке; странице нужен свой
            заголовок первого уровня, как и всем остальным разделам. */}
        <PageTitle title={ru.home.title} />

        <Text c="graphite.7" maw={620}>
          {isAuthenticated && user !== null
            ? `${ru.home.greeting}, ${user.firstName}. ${ru.home.lead}`
            : ru.home.lead}
        </Text>

        <Group gap="sm">
          {isAuthenticated ? (
            <Button component={Link} to="/profile">
              {ru.home.ctaProfile}
            </Button>
          ) : (
            <>
              <Button component={Link} to="/register">
                {ru.home.ctaRegister}
              </Button>
              <Button component={Link} to="/login" variant="default">
                {ru.nav.login}
              </Button>
            </>
          )}
        </Group>
      </Stack>

      <Stack gap="md">
        <Text
          size="xs"
          c="graphite.5"
          style={{ textTransform: 'uppercase', letterSpacing: '0.14em' }}
        >
          {ru.home.forParents}
        </Text>

        <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
          {sections.map((section) => {
            const body = (
              <Stack gap="xs">
                <Text fw={600} c="graphite.9">
                  {section.title}
                </Text>
                <Text size="sm" c="graphite.6" lh={1.5}>
                  {section.text}
                </Text>
              </Stack>
            );

            // Ссылкой делаем только разделы с готовым маршрутом, иначе
            // карточка вела бы на 404.
            return section.to !== null ? (
              <Card
                key={section.title}
                padding="lg"
                component={Link}
                to={section.to}
                style={{ textDecoration: 'none' }}
              >
                {body}
              </Card>
            ) : (
              <Card key={section.title} padding="lg">
                {body}
              </Card>
            );
          })}
        </SimpleGrid>
      </Stack>

      {apiIsDown && (
        <Group gap={8} align="center">
          <Box w={7} h={7} bg="red.6" style={{ borderRadius: '50%' }} />
          <Text size="xs" c="graphite.5">
            {ru.home.apiStatusTitle}: {ru.home.apiFail}
          </Text>
        </Group>
      )}
    </Stack>
  );
}
